import { LocaleValue } from "@eres/core/dist/LocaleValue";
import React, { Component } from "react";
import Strings, { LVStrings } from "@my-dld/assets/strings";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { Redirect } from "react-router";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import PageContent from "@my-dld/components/PageContent";
import ValuationForm from "./ValuationForm";
import ValuationCompanyRankingDetails from "./ValuationCompanyRankingDetails";
import { BaseTabuRequestProps } from "../TabuRequest/BaseTabuRequestForm";
import { ExValLookupData, ValuationCompany } from "../../valuationapi/entities";
import { ExternalValuationContext } from "@my-dld/contexts/ExternalValuationContext";
import SelectValuationCompany from "./SelectValuationCompany";
import { LocaleUtil } from "@eres/core";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import { extractPropInfo } from "../MyDLD/PropertyDetails";

type ExternalValuationComponent<P, S> = React.ComponentClass<P, S>

export enum TrackIndex {
	SELECT_VALUATION_COMPANY = 0,
	VALUATION_FORM = 1,
	VALUATION_COMPANY_RANKING_DETAILS = 2,
}

interface IExternalValuationTrack {
	index: number;
	name: LocaleValue;
	screen: ExternalValuationComponent<any, any>;
}

const ExternalValuationTrack: IExternalValuationTrack[] = [
	{
		index: TrackIndex.SELECT_VALUATION_COMPANY,
		name: LVStrings.valuation_company,
		screen: SelectValuationCompany,
	},
	{
		index: TrackIndex.VALUATION_FORM,
		name: LVStrings.evaluation_form,
		screen: ValuationForm,
	},
	{
		index: TrackIndex.VALUATION_COMPANY_RANKING_DETAILS,
		name: LVStrings.company_details,
		screen: ValuationCompanyRankingDetails,
	},
];

interface Props extends RoutedViewProps {

}

interface State {
	property: AssetCard;
	current: IExternalValuationTrack;
	showPopup?: boolean;
	procedureNumber?: string;
	valuationCompany?: ValuationCompany;
	valuationReason?: ExValLookupData;
	valuationScope?: ExValLookupData;
	isEmptyLand?: boolean;
}

export class ExternalValuation extends Component<Props, State> {
	static contextType = ExternalValuationContext;
	context!: React.ContextType<typeof ExternalValuationContext>;

	constructor( props: Props ) {
		super( props );
		const property = props.location.state ? props.location.state.property : undefined;
		this.state = {
			current: ExternalValuationTrack[ 0 ],
			property: property,
			isEmptyLand: true,
		};
		this.getValuationCompany = this.getValuationCompany.bind( this );
		this.emptyLand = this.emptyLand.bind( this );
		this.getValuationReason = this.getValuationReason.bind( this );
		this.getValuationScope = this.getValuationScope.bind( this );
	}

	render() {
		const { current, property } = this.state;
		if ( property == undefined )
			return <Redirect to={ "/mydld" }/>;
		const { val, lbl } = extractPropInfo( property );
		return <React.Fragment>
			<PageContent
				title={ `${ Strings.external_valuation } - ${ lbl } : ${ val }` }
				contentClass="col-md-12" hideQLinks={ true }>
				<PageTitle title={ Strings.external_valuation } paths={ [ DashboardBCItem ] }/>
				{ current && <this.state.current.screen
					holder={ this }
					property={ property }
					setValuationCompany={ this.setValuationCompany }
					getValuationCompany={ this.getValuationCompany }
					getValuationReason={ this.getValuationReason }
					setValuationReason={ this.setValuationReason }
					getValuationScope={ this.getValuationScope }
					setValuationScope={ this.setValuationScope }
					setEmptyLand={ this.setEmptyLand }
					emptyLand={ this.emptyLand }
					onPrevious={ this.onPrevious }
					onNext={ this.onNext }/> }
			</PageContent>
		</React.Fragment>;
	}

	private onPrevious = ( index: TrackIndex ) => {
		if ( index > 0 ) {
			this.setState( { current: ExternalValuationTrack[ index - 1 ] } );
		} else {
			this.setState( { current: ExternalValuationTrack[ 0 ] } );
		}
	};

	private onNext = ( index: TrackIndex ) => {
		if ( index <= ( ExternalValuationTrack.length - 1 ) ) {
			this.setState( { current: ExternalValuationTrack[ index ] } );
		}
	};

	private setValuationCompany = ( company: ValuationCompany ) => {
		this.setState( { valuationCompany: company } );
	};

	private getValuationCompany() {
		return this.state.valuationCompany;
	}

	private setEmptyLand = ( status: boolean ) => {
		this.setState( { isEmptyLand: status } );
	};

	private emptyLand() {
		return this.state.isEmptyLand;
	}

	private setValuationReason = ( reason: ExValLookupData ) => {
		this.setState( { valuationReason: reason } );
	};

	private getValuationReason() {
		return this.state.valuationReason;
	}

	private setValuationScope = ( reason: ExValLookupData ) => {
		this.setState( { valuationScope: reason } );
	};

	private getValuationScope() {
		return this.state.valuationScope;
	}
}

export interface ExternalValuationProps extends BaseTabuRequestProps {
	holder: any;
	property: AssetCard;
	setValuationCompany: ( company: ValuationCompany ) => void;
	getValuationCompany: () => ValuationCompany;
	setValuationReason: ( reason: ExValLookupData ) => void;
	getValuationReason: () => ExValLookupData;
	setValuationScope: ( reason: ExValLookupData ) => void;
	getValuationScope: () => ExValLookupData;
	setEmptyLand: ( status: boolean ) => void;
	emptyLand: () => boolean;
	onPrevious: ( index: TrackIndex ) => void;
	onNext: ( index: TrackIndex ) => void;
}

export function getUnitTypeAndSubType( property: any ) {
	let type = property.typeName ? property.typeName.englishName ? LocaleUtil.getLocalValue( property.typeName ) : "-" : "-";
	let subType = property.subTypeName ? property.subTypeName.englishName ? LocaleUtil.getLocalValue( property.subTypeName ) : "-" : "-";
	let typeSubType = "";
	if ( type || subType ) {
		if ( type === "-" && subType === "-" ) {
			typeSubType = "-";
		} else if ( type !== "-" && subType === "-" ) {
			typeSubType = type;
		} else if ( type === "-" && subType !== "-" ) {
			typeSubType = subType;
		} else if ( type !== "-" && subType !== "-" ) {
			typeSubType = type + " , " + subType;
		}
	} else {
		typeSubType = "-";
	}
	return typeSubType;
}
